import {createAsyncThunk} from '@reduxjs/toolkit'
import {
  FetchVideoGalleryRequest,
  FetchVideoGalleryByIdRequest,
  FetchAllVideoGalleryRequest,
  CreateVideoGalleryRequest,
  UpdateVideoGalleryRequest,
  DeleteVideoGalleryRequest,
} from './types'
import {AxiosError} from 'axios'
import {VideoGallery} from '../../../models'
import axios from 'axios'
import {
  fetchVideoGalleryUrl,
  fetchVideoGalleryByIdUrl,
  fetchAllVideoGalleryUrl,
  createVideoGalleryUrl,
  updateVideoGalleryUrl,
  deleteVideoGalleryUrl,
} from '../../api'

function buildListQuery(payload: FetchVideoGalleryRequest): string {
  const params = new URLSearchParams()
  params.set('sortField', payload.sortField)
  params.set('sortOrder', payload.sortOrder)
  params.set('pageNumber', String(payload.pageNumber))
  params.set('pageCount', String(payload.pageCount))
  params.set('role', payload.role)
  if (payload.teachers) params.set('teachers', payload.teachers)
  if (payload.classrooms) params.set('classrooms', payload.classrooms)
  if (payload.subjects) params.set('subjects', payload.subjects)
  if (payload.students) params.set('students', payload.students)
  return params.toString()
}

function buildAllQuery(payload: FetchAllVideoGalleryRequest): string {
  const params = new URLSearchParams()
  if (payload.teachers) params.set('teachers', payload.teachers)
  if (payload.classrooms) params.set('classrooms', payload.classrooms)
  if (payload.subjects) params.set('subjects', payload.subjects)
  if (payload.students) params.set('students', payload.students)
  const q = params.toString()
  return q ? `?${q}` : ''
}

export const fetchVideoGallery = createAsyncThunk(
  'videoGallery/fetch',
  async (payload: FetchVideoGalleryRequest, {rejectWithValue}) => {
    try {
      const query = buildListQuery(payload)
      const res = await axios.get(`${fetchVideoGalleryUrl}?${query}`)
      const {videos, totalPages, total} = res.data
      return {
        videos,
        totalPages,
        total,
        success: true,
      }
    } catch (error) {
      const err = error as AxiosError<{fr?: string; en?: string}>
      const message = err.response?.data?.fr ?? err.response?.data?.en ?? 'Erreur'
      return rejectWithValue({success: false, error: message})
    }
  }
)

export const fetchAllVideoGallery = createAsyncThunk(
  'videoGallery/fetchAll',
  async (payload: FetchAllVideoGalleryRequest, {rejectWithValue}) => {
    try {
      const query = buildAllQuery(payload)
      const res = await axios.get(`${fetchAllVideoGalleryUrl}${query}`)
      const {videos} = res.data
      return {videos, success: true}
    } catch (error) {
      const err = error as AxiosError<{fr?: string; en?: string}>
      const message = err.response?.data?.fr ?? err.response?.data?.en ?? 'Erreur'
      return rejectWithValue({success: false, error: message})
    }
  }
)

export const fetchVideoGalleryById = createAsyncThunk(
  'videoGallery/fetchById',
  async (payload: FetchVideoGalleryByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${fetchVideoGalleryByIdUrl}/${payload.id}`)
      const {video} = res.data
      return {video, success: true}
    } catch (error) {
      const err = error as AxiosError<{fr?: string; en?: string}>
      const message = err.response?.data?.fr ?? err.response?.data?.en ?? 'Erreur'
      return rejectWithValue({success: false, error: message})
    }
  }
)

export const createVideoGallery = createAsyncThunk(
  'videoGallery/create',
  async (payload: CreateVideoGalleryRequest, {rejectWithValue}) => {
    try {
      const body: Record<string, unknown> = {
        title: payload.title,
        description: payload.description ?? '',
        thumbnailUrl: payload.thumbnailUrl ?? '',
        durationSeconds: payload.durationSeconds,
        establishement: payload.establishement,
        subject: payload.subject,
        classrooms: payload.classrooms ?? [],
        toStudents: payload.toStudents ?? [],
        teacher: payload.teacher,
        specificStudents: payload.specificStudents ?? false,
        order: payload.order ?? 0,
      }
      if (payload.youtubeVideoId) body.youtubeVideoId = payload.youtubeVideoId
      if (payload.youtubeUrl) body.youtubeUrl = payload.youtubeUrl
      const res = await axios.post(createVideoGalleryUrl, body, {
        headers: {'Content-Type': 'application/json'},
      })
      const {video} = res.data
      return {video, success: true}
    } catch (error) {
      const err = error as AxiosError<{fr?: string; en?: string}>
      const message = err.response?.data?.fr ?? err.response?.data?.en ?? 'Erreur'
      return rejectWithValue({success: false, error: message})
    }
  }
)

export const updateVideoGallery = createAsyncThunk(
  'videoGallery/update',
  async (payload: UpdateVideoGalleryRequest, {rejectWithValue}) => {
    try {
      const body: Record<string, unknown> = {
        id: payload.id,
        title: payload.title,
        description: payload.description ?? '',
        thumbnailUrl: payload.thumbnailUrl ?? '',
        durationSeconds: payload.durationSeconds,
        establishement: payload.establishement,
        subject: payload.subject,
        classrooms: payload.classrooms ?? [],
        toStudents: payload.toStudents ?? [],
        teacher: payload.teacher,
        specificStudents: payload.specificStudents ?? false,
        order: payload.order ?? 0,
      }
      if (payload.youtubeVideoId) body.youtubeVideoId = payload.youtubeVideoId
      if (payload.youtubeUrl) body.youtubeUrl = payload.youtubeUrl
      const res = await axios.post(updateVideoGalleryUrl, body, {
        headers: {'Content-Type': 'application/json'},
      })
      const {video} = res.data
      return {video, success: true}
    } catch (error) {
      const err = error as AxiosError<{fr?: string; en?: string}>
      const message = err.response?.data?.fr ?? err.response?.data?.en ?? 'Erreur'
      return rejectWithValue({success: false, error: message})
    }
  }
)

export const deleteVideoGallery = createAsyncThunk(
  'videoGallery/delete',
  async (payload: DeleteVideoGalleryRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(deleteVideoGalleryUrl, {id: payload.id}, {
        headers: {'Content-Type': 'application/json'},
      })
      const {video} = res.data
      return {video, success: true}
    } catch (error) {
      const err = error as AxiosError<{fr?: string; en?: string}>
      const message = err.response?.data?.fr ?? err.response?.data?.en ?? 'Erreur'
      return rejectWithValue({success: false, error: message})
    }
  }
)
